import { useState } from 'react';
import { formatDuration, type PlayedItem } from '../lib/api';
import { youtubeThumbnailUrl } from '../lib/youtube';
import RemovalFlag from './RemovalFlag';
import './UpNext.css';
import './WatchedList.css';

function formatPlayedAt(value: string): string {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

export default function WatchedList({
  items,
  isAdmin,
  username,
  queuedVideoIds,
  nowPlayingVideoId,
  onRemove,
  onFlag,
  onQueue,
}: {
  items: PlayedItem[];
  isAdmin: boolean;
  username: string;
  queuedVideoIds: string[];
  nowPlayingVideoId?: string | null;
  onRemove: (id: string) => void;
  onFlag: (id: string, flagged: boolean) => void;
  onQueue: (youtubeUrl: string) => Promise<void>;
}) {
  const flaggedCount = items.filter((item) => (item.flag_count || 0) > 0).length;
  const queued = new Set(queuedVideoIds);
  const [busyId, setBusyId] = useState<string | null>(null);

  return (
    <details className="watched panel fade-up">
      <summary className="watched-summary">
        <h2 className="section-title">WATCHED</h2>
        <span className="watched-count">{items.length}</span>
        {isAdmin && flaggedCount > 0 && (
          <span className="watched-flagged" title={`${flaggedCount} flagged for removal`}>
            ⊘ {flaggedCount}
          </span>
        )}
      </summary>
      {items.length === 0 ? (
        <p className="muted">Nothing has been played yet.</p>
      ) : (
        <ol className="queue-list watched-list">
          {items.map((item) => {
            const thumb = youtubeThumbnailUrl(item.movie.youtube_video_id);
            const flaggedBy = item.flagged_by || [];
            const youtubeUrl = item.movie.youtube_url;
            const videoId = item.movie.youtube_video_id;
            const isPlaying = Boolean(nowPlayingVideoId && nowPlayingVideoId === videoId);
            const isQueued = queued.has(videoId);
            const busy = busyId === item.id;
            return (
              <li key={item.id} className="queue-item">
                {thumb && youtubeUrl && (
                  <a
                    className="watched-link watched-thumb-link"
                    href={youtubeUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    title="Open on YouTube"
                  >
                    <img
                      className="queue-thumb"
                      src={thumb}
                      alt=""
                      width={96}
                      height={54}
                    />
                  </a>
                )}
                {thumb && !youtubeUrl && (
                  <img
                    className="queue-thumb"
                    src={thumb}
                    alt=""
                    width={96}
                    height={54}
                  />
                )}
                <div className="queue-meta">
                  <strong className="queue-title">
                    {youtubeUrl ? (
                      <a
                        className="watched-link"
                        href={youtubeUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        title="Open on YouTube"
                      >
                        {item.movie.title}
                      </a>
                    ) : (
                      item.movie.title
                    )}
                    <RemovalFlag
                      isAdmin={isAdmin}
                      flagCount={item.flag_count || 0}
                      flaggedBy={flaggedBy}
                      flaggedByMe={flaggedBy.includes(username)}
                      onToggle={() => onFlag(item.id, !flaggedBy.includes(username))}
                    />
                  </strong>
                  <span className="muted">
                    {formatPlayedAt(item.played_at)}
                    {item.movie.duration_seconds
                      ? ` · ${formatDuration(item.movie.duration_seconds)}`
                      : ''}
                    {item.requested_by_username
                      ? ` · requested by ${item.requested_by_username}`
                      : ''}
                  </span>
                </div>
                <div className="queue-actions">
                  <button
                    className="btn btn-gold"
                    disabled={!youtubeUrl || isPlaying || isQueued || busy}
                    onClick={() => {
                      if (!youtubeUrl) return;
                      setBusyId(item.id);
                      void onQueue(youtubeUrl).finally(() => setBusyId(null));
                    }}
                  >
                    {isPlaying
                      ? 'Now Playing'
                      : isQueued
                        ? 'In Queue'
                        : busy
                          ? 'Adding…'
                          : 'Add to Queue'}
                  </button>
                  {isAdmin && (
                    <button className="btn" onClick={() => onRemove(item.id)}>
                      Remove
                    </button>
                  )}
                </div>
              </li>
            );
          })}
        </ol>
      )}
    </details>
  );
}
